/**
 * Ledger Versioning - One-time boot migration for SyncLedger and ACUUsageLedger.
 *
 * PROBLEM:
 *   SyncLedger (nuvra-sync-ledger) and ACUUsageLedger (nuvra-acu-usage-ledger)
 *   store raw JSON with no format version field. Any shape change to their
 *   entry types would silently corrupt sequence tracking for all open projects
 *   or ACU billing for all users.
 *
 * SOLUTION:
 *   This module runs at boot via initMigrationEngine().
 *   It adds a `formatVersion` field (default 1) to every entry that is missing one.
 *   All subsequent format changes to ledger entries are then tracked via version.
 *
 * RECOVERY POLICY:
 *   If a ledger entry has an unknown shape (not recognizable as v0 or current),
 *   it is reset to a fresh default entry and the event `state:corrupted` is emitted.
 *   This is safe because:
 *     - SyncLedger is reconstructable from the OfflineJournal on next sync
 *     - ACUUsageLedger losses are reported to the bridge which re-discovers
 *       pending entries from the ACU offline journal
 *
 * CURRENT LEDGER FORMAT VERSIONS:
 *   SyncLedger:     v1 (adds formatVersion field to each SyncLedgerEntry)
 *   ACUUsageLedger: v1 (adds formatVersion field to each ACUUsageLedgerEntry)
 */

import { logger }   from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";

const SYNC_LEDGER_KEY = "nuvra-sync-ledger";
const ACU_LEDGER_KEY  = "nuvra-acu-usage-ledger";

const CURRENT_SYNC_LEDGER_FORMAT    = 1;
const CURRENT_ACU_LEDGER_FORMAT     = 1;

/* =========================================================
   SYNC LEDGER VERSIONING
   ========================================================= */

interface SyncLedgerEntryV1 {
  projectId:            string;
  localSequenceNum:     number;
  cloudSequenceNum:     number;
  lastSyncedAt:         number;
  status:               string;
  pendingCount:         number;
  consecutiveFailures:  number;
  lastError:            string | null;
  formatVersion:        number;  // NEW: added by this migration
}

function defaultSyncEntry(projectId: string): SyncLedgerEntryV1 {
  return {
    projectId,
    localSequenceNum:    0,
    cloudSequenceNum:    0,
    lastSyncedAt:        0,
    status:              "synced",
    pendingCount:        0,
    consecutiveFailures: 0,
    lastError:           null,
    formatVersion:       CURRENT_SYNC_LEDGER_FORMAT,
  };
}

function migrateSyncLedgerEntry(raw: Record<string, unknown>, projectId: string): SyncLedgerEntryV1 {
  // Already at current version
  if (
    typeof raw.formatVersion === "number" &&
    raw.formatVersion === CURRENT_SYNC_LEDGER_FORMAT
  ) {
    return raw as unknown as SyncLedgerEntryV1;
  }

  // v0: missing formatVersion - these are pre-migration entries
  if (!("formatVersion" in raw)) {
    return {
      projectId:           (raw.projectId           as string)  ?? projectId,
      localSequenceNum:    (raw.localSequenceNum     as number)  ?? 0,
      cloudSequenceNum:    (raw.cloudSequenceNum     as number)  ?? 0,
      lastSyncedAt:        (raw.lastSyncedAt         as number)  ?? 0,
      status:              (raw.status               as string)  ?? "synced",
      pendingCount:        (raw.pendingCount         as number)  ?? 0,
      consecutiveFailures: (raw.consecutiveFailures  as number)  ?? 0,
      lastError:           (raw.lastError            as string | null) ?? null,
      formatVersion:       CURRENT_SYNC_LEDGER_FORMAT,
    };
  }

  // Unknown version - reset to safe default
  logger.error("ledgerVersioning: unrecognised SyncLedgerEntry format", {
    projectId,
    formatVersion: raw.formatVersion,
  });
  eventBus.emit({ type: "state:corrupted", projectId, reason: "SyncLedger entry had unknown format version" });
  return defaultSyncEntry(projectId);
}

export function migrateSyncLedger(): void {
  if (typeof localStorage === "undefined") return;

  try {
    const raw = localStorage.getItem(SYNC_LEDGER_KEY);
    if (!raw) return;

    const store = JSON.parse(raw) as Record<string, Record<string, unknown>>;
    let   changed = false;

    for (const [projectId, entry] of Object.entries(store)) {
      if (!entry || typeof entry !== "object") {
        store[projectId] = defaultSyncEntry(projectId) as unknown as Record<string, unknown>;
        changed = true;
        continue;
      }

      if (!("formatVersion" in entry)) {
        store[projectId] = migrateSyncLedgerEntry(entry, projectId) as unknown as Record<string, unknown>;
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem(SYNC_LEDGER_KEY, JSON.stringify(store));
      logger.info("ledgerVersioning: SyncLedger migrated to v1");
    }
  } catch (err) {
    logger.error("ledgerVersioning: SyncLedger migration failed", { err });
  }
}

/* =========================================================
   ACU USAGE LEDGER VERSIONING
   ========================================================= */

interface ACULedgerEntryV1 {
  userId:               string;
  localSequenceNum:     number;
  cloudSequenceNum:     number;
  lastSyncedAt:         number;
  status:               string;
  pendingCount:         number;
  consecutiveFailures:  number;
  lastError:            string | null;
  formatVersion:        number;  // NEW: added by this migration
}

function defaultACUEntry(userId: string): ACULedgerEntryV1 {
  return {
    userId,
    localSequenceNum:    0,
    cloudSequenceNum:    0,
    lastSyncedAt:        0,
    status:              "synced",
    pendingCount:        0,
    consecutiveFailures: 0,
    lastError:           null,
    formatVersion:       CURRENT_ACU_LEDGER_FORMAT,
  };
}

function migrateACULedgerEntry(raw: Record<string, unknown>, userId: string): ACULedgerEntryV1 {
  if (
    typeof raw.formatVersion === "number" &&
    raw.formatVersion === CURRENT_ACU_LEDGER_FORMAT
  ) {
    return raw as unknown as ACULedgerEntryV1;
  }

  if (!("formatVersion" in raw)) {
    return {
      userId:              (raw.userId               as string) ?? userId,
      localSequenceNum:    (raw.localSequenceNum     as number) ?? 0,
      cloudSequenceNum:    (raw.cloudSequenceNum     as number) ?? 0,
      lastSyncedAt:        (raw.lastSyncedAt         as number) ?? 0,
      status:              (raw.status               as string) ?? "synced",
      pendingCount:        (raw.pendingCount         as number) ?? 0,
      consecutiveFailures: (raw.consecutiveFailures  as number) ?? 0,
      lastError:           (raw.lastError            as string | null) ?? null,
      formatVersion:       CURRENT_ACU_LEDGER_FORMAT,
    };
  }

  logger.error("ledgerVersioning: unrecognised ACULedgerEntry format", {
    userId,
    formatVersion: raw.formatVersion,
  });
  eventBus.emit({ type: "state:corrupted", projectId: `acu-${userId}`, reason: "ACUUsageLedger entry had unknown format version" });
  return defaultACUEntry(userId);
}

export function migrateACUUsageLedger(): void {
  if (typeof localStorage === "undefined") return;

  try {
    const raw = localStorage.getItem(ACU_LEDGER_KEY);
    if (!raw) return;

    const store = JSON.parse(raw) as Record<string, Record<string, unknown>>;
    let   changed = false;

    for (const [userId, entry] of Object.entries(store)) {
      if (!entry || typeof entry !== "object") {
        store[userId] = defaultACUEntry(userId) as unknown as Record<string, unknown>;
        changed = true;
        continue;
      }

      if (!("formatVersion" in entry)) {
        store[userId] = migrateACULedgerEntry(entry, userId) as unknown as Record<string, unknown>;
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem(ACU_LEDGER_KEY, JSON.stringify(store));
      logger.info("ledgerVersioning: ACUUsageLedger migrated to v1");
    }
  } catch (err) {
    logger.error("ledgerVersioning: ACUUsageLedger migration failed", { err });
  }
}
